import React from 'react';
import PropTypes from 'prop-types';

import { Grid } from '@mui/material';

import { useFormState } from 'shared/hooks';
import useConsultData from 'features/consults/hooks/useConsultData';
import useConsultInitialFormState from 'features/consults/hooks/useConsultInitialFormState';
import useSharedHandlers from 'features/consults/hooks/useSharedHandlers';

import DatePicker from 'shared/components/forms/DatePicker/DatePicker';
import GridWrapper from 'shared/components/layout/GridWrapper/GridWrapper';
import QuestionsPanel from 'features/consults/components/tabFields/QuestionsPanel';
import Autocomplete from 'shared/components/forms/Autocomplete';
import TextInput from 'features/consults/components/tabFields/TextInput/TextInput';
import { CONSULT_TABS } from 'features/consults/utils/constants';
import formFields, {
  phoneAgreementValue,
} from 'features/consults/shared/tabs/Demographics/formFields';
import { Box } from 'styles/layout';

import schema from './validationSchema';
import {
  leftPanel1,
  leftPanel2,
  leftPanel3,
  rightPanel,
  phoneOnlyPanel,
  phoneOnlyExtraPanel,
} from './data';

const {
  firstName,
  lastName,
  dateOfBirth,
  facility,
  phoneOnly,
  phoneOnlyFreetext,
  phoneAgreement,
} = formFields;

const Demographics = ({ errors }) => {
  const {
    control,
    clearErrors,
    getFieldError,
    getValues,
    register,
    setValue,
    setFormValues,
    watch,
    trigger,
  } = useFormState(schema, { stateSlice: 'consults', showAsSnackbar: true });
  const {
    consult,
    consultTabData,
    consultMainType,
    isEditable,
    isMacro,
    handleFieldChange,
    saveConsultChange,
  } = useConsultData(CONSULT_TABS.DEMOGRAPHICS, getValues);
  const {
    facilities,
    handleNameChange,
    handleFacilityChange,
    handleDateOfBirthChange,
  } = useSharedHandlers(
    consult.id,
    consultMainType,
    saveConsultChange,
    setValue,
    getValues,
  );
  const phoneOnlySection = watch(phoneOnly.name);

  useConsultInitialFormState(consult.id, consultTabData, setFormValues);

  React.useEffect(() => {
    clearErrors();
    if (errors) trigger(errors);
  }, [errors, clearErrors, trigger]);

  const isDisabled = React.useMemo(
    () => isMacro || !isEditable,
    [isMacro, isEditable],
  );

  const handlePhoneOnlyChange = (fieldName) => {
    const fieldValue = getValues(fieldName);
    const agreementValue = fieldValue?.length ? [phoneAgreementValue] : [];

    setValue(phoneAgreement.name, agreementValue);
    saveConsultChange({
      [fieldName]: fieldValue,
      [phoneOnlyFreetext.name]: null,
      [phoneAgreement.name]: agreementValue,
    });
  };

  return (
    <GridWrapper spacing={10}>
      <Grid item sm={12} lg={7}>
        <QuestionsPanel
          elements={leftPanel1}
          control={control}
          register={register}
          getFieldError={getFieldError}
          onChange={handleFieldChange}
          disabled={isDisabled}
        />
        <Box mb={1}>
          <TextInput
            register={register}
            name={firstName.name}
            label={firstName.label}
            required
            disabled={isDisabled}
            getFieldError={getFieldError}
            onChange={handleNameChange}
          />
        </Box>
        <Box mb={1}>
          <TextInput
            register={register}
            label={lastName.label}
            name={lastName.name}
            required
            disabled={isDisabled}
            getFieldError={getFieldError}
            onChange={handleNameChange}
          />
        </Box>
        <Box mb={1}>
          <DatePicker
            control={control}
            name={dateOfBirth.name}
            label={dateOfBirth.label}
            error={getFieldError(dateOfBirth.name)}
            disableFuture
            required
            handleChange={handleDateOfBirthChange}
            disabled={isDisabled}
          />
        </Box>
        <QuestionsPanel
          elements={leftPanel2}
          control={control}
          register={register}
          getFieldError={getFieldError}
          onChange={handleFieldChange}
          disabled={isDisabled}
        />
        <Box mb={1}>
          <Autocomplete
            control={control}
            disabled={isDisabled || isMacro}
            name={facility.name}
            label={facility.label}
            required
            options={facilities}
            handleChange={(value) => {
              handleFacilityChange(value);
              handleFieldChange(facility.name);
            }}
            error={getFieldError(facility.name)}
          />
        </Box>
        <QuestionsPanel
          elements={leftPanel3}
          control={control}
          register={register}
          getFieldError={getFieldError}
          onChange={handleFieldChange}
          disabled={isDisabled}
        />
      </Grid>
      <Grid item sm={12} lg={5}>
        <QuestionsPanel
          elements={rightPanel}
          control={control}
          register={register}
          getFieldError={getFieldError}
          onChange={handleFieldChange}
          disabled={isDisabled}
        />
        <QuestionsPanel
          elements={phoneOnlyPanel}
          control={control}
          register={register}
          onChange={handlePhoneOnlyChange}
          getFieldError={getFieldError}
          disabled={isDisabled}
        />
        {!!phoneOnlySection?.length && (
          <QuestionsPanel
            elements={phoneOnlyExtraPanel}
            control={control}
            register={register}
            onChange={handleFieldChange}
            getFieldError={getFieldError}
            disabled={isDisabled}
          />
        )}
      </Grid>
    </GridWrapper>
  );
};

Demographics.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
};

export default Demographics;
